import { DocsService } from '@affine/core/modules/doc';
import {
  EditorSettingService,
  getDefaultPageWidth,
  matchPageWidthArgument,
  type PageWidth,
  resolvePageWidth,
} from '@affine/core/modules/editor-setting';
import {
  type SlashMenuActionContext,
  type SlashMenuActionItem,
  SlashMenuConfigExtension,
  type SlashMenuContext,
} from '@blocksuite/affine/widgets/slash-menu';
import {
  DocModeProvider,
  NotificationProvider,
} from '@blocksuite/affine-shared/services';
import { LongerIcon } from '@blocksuite/icons/lit';
import type { ExtensionType } from '@blocksuite/store';
import type { FrameworkProvider } from '@toeverything/infra';

const ICON_SIZE = { width: '20px', height: '20px' } as const;

const WIDTH_LABELS: Record<PageWidth, { name: string; description: string }> = {
  standard: { name: 'Standard', description: 'Fixed reading width' },
  wide: { name: 'Wide', description: 'Grows with the window' },
  fullWidth: { name: 'Full width', description: 'Fills the window' },
};

const HINT = 'Try /width standard, /width wide or /width full';

export function patchWidthSlashMenu(
  framework: FrameworkProvider
): ExtensionType {
  const docsService = framework.get(DocsService);
  const editorSetting = framework.get(EditorSettingService).editorSetting;

  const toast = (ctx: SlashMenuContext, message: string) => {
    ctx.std.getOptional(NotificationProvider)?.toast(message);
  };

  const docRecordOf = (ctx: SlashMenuContext) =>
    docsService.list.doc$(ctx.std.store.id).value;

  const currentWidth = (ctx: SlashMenuContext): PageWidth =>
    resolvePageWidth(
      docRecordOf(ctx)?.properties$.value.pageWidth,
      getDefaultPageWidth(editorSetting.settings$.value)
    );

  const applyWidth = (ctx: SlashMenuContext, width: PageWidth) => {
    const docRecord = docRecordOf(ctx);
    if (!docRecord) {
      toast(ctx, 'Cannot change the width of this page');
      return;
    }
    docRecord.setProperty('pageWidth', width);
    toast(ctx, `Page width: ${WIDTH_LABELS[width].name}`);
  };

  const getWidthSuggestions = (
    ctx: SlashMenuContext,
    actionContext: SlashMenuActionContext
  ): SlashMenuActionItem[] => {
    const current = currentWidth(ctx);
    return matchPageWidthArgument(actionContext.argument).map(width => ({
      name: WIDTH_LABELS[width].name,
      description:
        width === current
          ? `${WIDTH_LABELS[width].description} · Current`
          : WIDTH_LABELS[width].description,
      icon: LongerIcon(ICON_SIZE),
      action: (actionCtx: SlashMenuContext) => applyWidth(actionCtx, width),
    }));
  };

  return SlashMenuConfigExtension('affine-page-width', {
    items: [
      {
        name: 'Width',
        icon: LongerIcon(ICON_SIZE),
        description: 'Set how wide this page is',
        group: '3_Page@2',
        searchAlias: ['width', 'pagewidth', 'page width'],
        acceptsArgument: true,
        when: ctx =>
          !BUILD_CONFIG.isMobileEdition &&
          ctx.std.get(DocModeProvider).getEditorMode() !== 'edgeless',
        action: (ctx, actionContext) => {
          const [match, ...rest] = matchPageWidthArgument(
            actionContext?.argument ?? ''
          );
          if (!match || rest.length > 0) {
            toast(ctx, HINT);
            return;
          }
          applyWidth(ctx, match);
        },
        getArgumentItems: getWidthSuggestions,
      },
    ],
  });
}
